import React, { useState, useEffect } from 'react';
import { User, Calendar, Activity, ChevronLeft, ChevronRight, Filter, BarChart3, Eye, Tent } from 'lucide-react';

const UserStatsReport = () => {
    const [statsData, setStatsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        total: 0,
        limit: 25,
        offset: 0,
        has_more: false
    });
    const [stats, setStats] = useState({
        unique_users: 0,
        total_accesses: 0,
        unique_endpoints: 0,
        latest_access: null,
        earliest_record: null
    });
    const [filters, setFilters] = useState({
        user_id: '',
        time_filter: '',
        endpoint: 'getOTGEvents.php'
    });
    const [showFilters, setShowFilters] = useState(false);

    const timeFilterOptions = [
        { value: '', label: 'All Time' },
        { value: 'day', label: 'Last 24 Hours' },
        { value: 'week', label: 'Last Week' },
        { value: 'month', label: 'Last Month' },
        { value: 'year', label: 'Last Year' }
    ];

    const endpointOptions = [
        { value: '', label: 'All Endpoints' },
        { value: 'getOTGEvents.php', label: 'Get OTG Events' },
        { value: 'createEvent.php', label: 'Create Event' },
        { value: 'editEvent.php', label: 'Edit Event' },
        { value: 'joinEvent.php', label: 'Join Event' }
    ];

    const fetchStatsData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: pagination.limit,
                offset: pagination.offset,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, value]) => value !== '')
                )
            });

            const response = await fetch(`/otgservices/getUserStats.php?${params}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.success) {
                setStatsData(data.data);
                setPagination(data.pagination);
                setStats(data.stats);
                setError(null);
            } else {
                throw new Error(data.error || 'Failed to fetch user stats');
            }
        } catch (err) {
            console.error('Error fetching user stats:', err);
            setError(err.message);
            setStatsData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatsData();
    }, [pagination.limit, pagination.offset, filters]);

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
        setPagination(prev => ({ ...prev, offset: 0 }));
    };

    const handlePageChange = (newOffset) => {
        setPagination(prev => ({ ...prev, offset: newOffset }));
    };

    const formatDateTime = (dateTime) => {
        if (!dateTime) return 'N/A';
        return new Date(dateTime).toLocaleString();
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat().format(num);
    };

    if (loading && statsData.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <div className="flex items-center">
                            <div className="text-red-600 mr-3">⚠️</div>
                            <div>
                                <h3 className="text-lg font-medium text-red-800">Error Loading User Stats</h3>
                                <p className="text-red-700 mt-1">{error}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-orange-600 text-white px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded">
                            <Tent className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold">OTG Camp</h1>
                            <p className="text-sm opacity-90">User Statistics</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <span className="text-sm bg-orange-700 px-2 py-1 rounded">Pro</span>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-6">

                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <User className="h-8 w-8 text-blue-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Unique Users</p>
                                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.unique_users)}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <Eye className="h-8 w-8 text-green-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Total Accesses</p>
                                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.total_accesses)}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <Activity className="h-8 w-8 text-purple-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Endpoints</p>
                                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.unique_endpoints)}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <Calendar className="h-8 w-8 text-orange-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Latest Access</p>
                                <p className="text-sm font-bold text-gray-900">
                                    {stats.latest_access ? new Date(stats.latest_access).toLocaleDateString() : 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <Calendar className="h-8 w-8 text-indigo-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">First Record</p>
                                <p className="text-sm font-bold text-gray-900">
                                    {stats.earliest_record ? new Date(stats.earliest_record).toLocaleDateString() : 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="px-6 py-4 border-b">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="flex items-center text-lg font-medium text-gray-900"
                        >
                            <Filter className="mr-2" />
                            Filters
                            <ChevronRight className={`ml-2 transform transition-transform ${showFilters ? 'rotate-90' : ''}`} />
                        </button>
                    </div>
                    
                    {showFilters && (
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">User ID</label>
                                    <input
                                        type="number"
                                        placeholder="Enter user ID"
                                        value={filters.user_id}
                                        onChange={(e) => handleFilterChange('user_id', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Time Period</label>
                                    <select
                                        value={filters.time_filter}
                                        onChange={(e) => handleFilterChange('time_filter', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {timeFilterOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Endpoint</label>
                                    <select
                                        value={filters.endpoint}
                                        onChange={(e) => handleFilterChange('endpoint', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {endpointOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Data Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b">
                        <h2 className="text-lg font-medium text-gray-900">
                            Access Records ({formatNumber(pagination.total)} total)
                        </h2>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endpoint</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Count</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Access</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Access</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {statsData.map((record) => (
                                    <tr key={record.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10">
                                                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                        <User className="h-5 w-5 text-gray-500" />
                                                    </div>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {record.username}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        ID: {record.user_id}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                {record.endpoint}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                {formatNumber(record.access_count)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDateTime(record.last_access_time)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDateTime(record.created_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {statsData.length === 0 && !loading && (
                        <div className="text-center py-12">
                            <div className="text-gray-500">No user stats found matching your criteria.</div>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {pagination.total > 0 && (
                    <div className="bg-white px-6 py-4 flex items-center justify-between border-t border-gray-200 mt-6 rounded-lg shadow">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
                                disabled={pagination.offset === 0}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                                disabled={!pagination.has_more}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Showing <span className="font-medium">{pagination.offset + 1}</span> to{' '}
                                    <span className="font-medium">
                                        {Math.min(pagination.offset + pagination.limit, pagination.total)}
                                    </span>{' '}
                                    of <span className="font-medium">{formatNumber(pagination.total)}</span> results
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                    <button
                                        onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
                                        disabled={pagination.offset === 0}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        <ChevronLeft className="h-5 w-5" />
                                    </button>
                                    <button
                                        onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                                        disabled={!pagination.has_more}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        <ChevronRight className="h-5 w-5" />
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserStatsReport;